import { trackPromise } from "react-promise-tracker";
import { MENU2, MENU3, MENU4, MENU5, MENU6, MENU7, MENU8, MENU10 } from "../../modules/config";
import UserService from "../../services/UserService";
import BimProfileService from "../../services/BimProfileService";
import TreeComponentTypeService from "../../services/TreeComponentTypeService";
import ProjectService from "../../services/ProjectService";
import http from "../../shared/http-common";
import { buildRealmPath } from "../../shared/routing";

/**
 * Baut das Menü-Handler-Objekt (Strategy Pattern).
 * Jeder Handler ist eine async-Funktion, die Daten lädt und den App-State aktualisiert.
 *
 * @param {object} deps - Abhängigkeiten (Token, Realm, Setter-Funktionen, Zustandswerte)
 */
export function buildMenuHandlers({
    token,
    theRealm,
    treeItemState,
    profilIsLoaded,
    // Setter
    setTreeItemsState,
    setTreeClicked,
    setTreeIsLoaded,
    setProfilClicked,
    setProfilIsLoaded,
    setProjectClicked,
    setProjectIsLoaded,
    setUserProjectIsLoaded,
    setDataobjectsClicked,
    setDataobjectsIsLoaded,
    setDataobjects,
    setDatacomponentsClicked,
    setBimProf,
    setBimProjects,
    setBimUserProjects,
}) {
    // --- Hilfsfunktionen (intern, alle async/await mit Fehlerbehandlung) ---

    const loadTreeData = async () => {
        try {
            const res = await TreeComponentTypeService.getTreeData(token, theRealm);
            setTreeItemsState(res.data);
            setTreeIsLoaded(true);
        } catch (e) {
            console.error("Fehler beim Laden des Katalogs:", e);
        }
    };

    const loadTreeDataIfNeeded = async () => {
        if (!treeItemState || treeItemState.length === 0) {
            await loadTreeData();
        }
    };

    const loadProfilesData = async () => {
        try {
            const res = await BimProfileService.getBimProfiles(token, theRealm);
            setBimProf(res.data);
            setProfilIsLoaded(true);
        } catch (e) {
            console.error("Fehler beim Laden der Profile:", e);
        }
    };

    const loadProjectsData = async () => {
        try {
            const formData = new FormData();
            formData.append("realm", theRealm);
            const res = await http.post("/project/listAll", formData, {
                headers: {
                    "Content-Type": "application/json",
                },
            });
            setBimProjects(res.data);
            setProjectIsLoaded(true);
        } catch (e) {
            console.error("Fehler beim Laden der Projekte:", e);
        }
    };

    const loadObjectData = async () => {
        try {
            const formData = new FormData();
            formData.append("realm", theRealm);
            const res = await http.post("/administrativeobject/economicunit/list", formData, {
                headers: {
                    "Content-Type": "application/json",
                },
            });
            setDataobjects(res.data);
            setDataobjectsIsLoaded(true);
        } catch (e) {
            console.error("Fehler beim Laden der Daten-Objekte:", e);
        }
    };

    const resetViewFlags = (flags = {}) => {
        setTreeClicked(flags.tree ?? false);
        setProfilClicked(flags.profil ?? false);
        setProjectClicked(flags.project ?? false);
        setDataobjectsClicked(flags.dataobjects ?? false);
        setDatacomponentsClicked(flags.datacomponents ?? false);
    };

    // --- Menü-Handler ---

    return {
        [MENU2]: async () => {
            resetViewFlags({ tree: true });
            await trackPromise(loadTreeData());
        },

        [MENU3]: async () => {
            if (!UserService.isLoggedIn()) { UserService.doLogout(); return; }
            resetViewFlags({ profil: true });
            await trackPromise(loadProfilesData());
            await loadTreeDataIfNeeded();
        },

        [MENU4]: async () => {
            if (!UserService.isLoggedIn()) { UserService.doLogout(); return; }
            resetViewFlags({ project: true });
            await trackPromise(loadProjectsData());
            if (!profilIsLoaded) await loadProfilesData();
        },

        [MENU5]: async () => {
            await UserService.doLogout();
            window.location.href = buildRealmPath(theRealm, "/logout");
        },

        [MENU6]: async () => {
            resetViewFlags({ dataobjects: true });
            await trackPromise(loadObjectData());
            await loadTreeDataIfNeeded();
        },

        [MENU7]: async () => {
            resetViewFlags({ datacomponents: true });
            await loadTreeDataIfNeeded();
        },

        [MENU8]: async () => {
            if (!UserService.isLoggedIn()) { UserService.doLogout(); return; }
            resetViewFlags({ project: true });
            await trackPromise(
                ProjectService.listmyprojects(token, theRealm).then((res) => {
                    setBimUserProjects(res?.data);
                    setUserProjectIsLoaded(true);
                })
            );
        },

        [MENU10]: async () => {
            resetViewFlags({});
            await loadTreeDataIfNeeded();
        },
    };
}
